import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	Link,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import {GetPrivacyPolicyLinkParams} from 'domain/usecases';
import {makeGetPrivacyPolicyLink} from 'main/factories/usecases';
import {AlertInfo} from 'presentation/components';
import {useGetEndpointUrl, useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';
import {translations} from 'presentation/translations-new';
import React, {PropsWithChildren, useCallback, useState} from 'react';
import {DrawerContent, ScrollableContent, StyledDrawer} from './styles';
import {ConsentTermDrawerProps} from './types';

const {consentSheet} = translations['pt-br'].privateFlow.profile.dataSharing;
const {privacyPolicy, understood} = translations['pt-br'].commons;

function ConsentAccordion({title, children}: PropsWithChildren<{title: string}>) {
	const theme = useTheme();

	return (
		<Accordion
			disableGutters
			elevation={0}
			sx={{
				backgroundColor: theme.palette.grey[50],
				borderRadius: 1,
				'&::before': {display: 'none'},
				'&.Mui-expanded': {margin: 0},
			}}>
			<AccordionSummary
				expandIcon={
					<Icon name="expand-more-filled" color={theme.palette.primary.main} size={20} />
				}
				sx={{px: 2, minHeight: 56, '& .MuiAccordionSummary-content': {my: 1.5}}}>
				<Typography variant="body2" fontWeight={600}>
					{title}
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{px: 2, pt: 0, pb: 2}}>
				<Stack gap={1}>{children}</Stack>
			</AccordionDetails>
		</Accordion>
	);
}

export function ConsentTermDrawer({open, onClose, onAccept, readonly}: ConsentTermDrawerProps) {
	const theme = useTheme();
	const {isMobile} = useMediaQueries();
	const [checked, setChecked] = useState(false);
	const {getLink, isLoading} = useGetEndpointUrl(privacyPolicy);
	const cachedURL = useExternalServiceStore(state => state.urls[privacyPolicy]);
	const {
		dataHostNameState: {productCode},
		isNotredameSaude,
	} = useHostName();

	const handleAccept = () => {
		if (!checked && !readonly) return;
		onAccept();
		setChecked(false);
	};

	const handleClose = () => {
		setChecked(false);
		onClose();
	};

	const handleOpenPrivacyPolicy = useCallback(() => {
		if (cachedURL) {
			window.open(cachedURL, '_blank', 'noopener noreferrer');
			return;
		}

		const cdEmpresaPlano = productCode?.[0] || 0;
		const params: GetPrivacyPolicyLinkParams = {cdEmpresaPlano, isPpo: isNotredameSaude};
		getLink(makeGetPrivacyPolicyLink, false, false, params);
	}, [cachedURL, getLink, isNotredameSaude, productCode]);

	return (
		<StyledDrawer
			anchor={isMobile ? 'bottom' : 'right'}
			open={open}
			onClose={handleClose}
			slotProps={{
				paper: {
					sx: {
						borderTopLeftRadius: isMobile ? 8 : 0,
						borderTopRightRadius: isMobile ? 8 : 0,
						maxHeight: isMobile ? 'calc(100% - 16px)' : '100%',
					},
				},
			}}>
			<DrawerContent $isMobile={isMobile}>
				{isMobile ? (
					<Box
						sx={{
							alignSelf: 'center',
							backgroundColor: theme.palette.grey[300],
							borderRadius: 2,
							height: 4,
							mt: 1,
							width: 32,
						}}
					/>
				) : null}

				<Stack direction="row" justifyContent="flex-end" px={1} pt={1}>
					<IconButton onClick={handleClose} aria-label="Fechar termo de consentimento">
						<Icon name="close-filled" color={theme.palette.grey[600]} size={24} />
					</IconButton>
				</Stack>

				<ScrollableContent sx={{px: 3, pb: 3}}>
					<Stack gap={2}>
						<Typography variant="h6" fontWeight={600}>
							{consentSheet.title}
						</Typography>
						<Typography variant="body2">{consentSheet.intro}</Typography>

						<AlertInfo.Root
							severity="info"
							icon={<Icon name="info-outlined" size={20} />}>
							<Typography variant="body2">
								{consentSheet.alertPrefix}
								<strong>{consentSheet.alertHighlight}</strong>
								{consentSheet.alertSuffix}
							</Typography>
						</AlertInfo.Root>

						<ConsentAccordion title={consentSheet.dataAccordion.title}>
							<Typography variant="body2">
								{consentSheet.dataAccordion.intro}
							</Typography>
							<Box component="ul" sx={{m: 0, pl: 3, listStyle: 'disc'}}>
								{consentSheet.dataAccordion.items.map(item => (
									<Typography variant="body2" component="li" key={item}>
										{item}
									</Typography>
								))}
							</Box>
						</ConsentAccordion>

						<ConsentAccordion title={consentSheet.purposeAccordion.title}>
							<Typography variant="body2">
								{consentSheet.purposeAccordion.description}
							</Typography>
						</ConsentAccordion>

						<ConsentAccordion title={consentSheet.revocationAccordion.title}>
							<Typography variant="body2">
								{consentSheet.revocationAccordion.firstParagraph}
							</Typography>
							<Typography variant="body2">
								{consentSheet.revocationAccordion.secondParagraph}
							</Typography>
							<Typography variant="body2">
								{consentSheet.revocationAccordion.privacyIntro}
							</Typography>
							<Link
								component="button"
								disabled={isLoading}
								onClick={handleOpenPrivacyPolicy}
								sx={{
									alignItems: 'center',
									alignSelf: 'flex-start',
									display: 'flex',
									gap: 1,
								}}>
								<Icon
									name="ben-export"
									color={theme.palette.primary.main}
									size={16}
								/>
								{consentSheet.revocationAccordion.privacyLink}
							</Link>
						</ConsentAccordion>

						<Typography variant="caption">{consentSheet.footnote}</Typography>
					</Stack>
				</ScrollableContent>

				<Stack
					sx={{
						alignItems: 'stretch',
						borderTop: `1px solid ${theme.palette.divider}`,
						flexDirection: 'column',
						gap: 1,
						p: 2,
					}}>
					{readonly ? null : (
						<FormControlLabel
							sx={{alignItems: 'flex-start', m: 0}}
							control={
								<Checkbox
									checked={checked}
									onChange={event => setChecked(event.target.checked)}
								/>
							}
							label={<Typography variant="body1">{consentSheet.checkbox}</Typography>}
						/>
					)}
					<Button
						variant="contained"
						size="large"
						fullWidth
						disabled={!checked && !readonly}
						onClick={handleAccept}>
						{readonly ? understood : consentSheet.confirm}
					</Button>
				</Stack>
			</DrawerContent>
		</StyledDrawer>
	);
}
